import { useState, useEffect, useMemo } from "react";
import { FormGroup, Label, Input, Col, Row } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
import axios from "../../services/api/api";

const CreateNewScormModuleForm = ({
  setModule,
  module,
  errors,
  setErrors,
  moduleToEdit,
  gy25Modules,
}) => {
  const intl = useIntl();
  const [qApiIdCheckResult, setQApiIdCheckResult] = useState({});
  const handleSetName = (name) => {
    setErrors(errors.filter((obj) => obj !== "name"));
    setModule({
      ...module,
      name: name,
    });
  };

  const handleSetDescription = (description) => {
    setErrors(errors.filter((obj) => obj !== "description"));
    setModule({
      ...module,
      description: description,
    });
  };

  const showPreviewCheckbox =
    moduleToEdit === null || moduleToEdit.isPreview;

  const handleTogglePreview = (checked) => {
    setErrors(errors.filter((obj) => obj !== "previewText"));
    if (checked) {
      setModule({
        ...module,
        isPreview: true,
        isFinalExam: false,
        isFactModule: true,
        moduleCoupleId: null,
        qApiId: "",
        maxScore: 0,
        maxActivityPoints: 0,
        approveScoreLimit: 0,
        maxScoreIsSetByModule: true,
        file: null,
        answers: null,
      });
    } else {
      setModule({
        ...module,
        isPreview: false,
        previewText: null,
      });
    }
  };

  const handleSetPreviewText = (text) => {
    setErrors(errors.filter((obj) => obj !== "previewText"));
    setModule({ ...module, previewText: text });
  };

  const factModuleDisabled = useMemo(
    () => !module.maxScoreIsSetByModule || module.isFinalExam,
    [module.isFinalExam, module.maxScoreIsSetByModule],
  );

  const qApiIdDisabled = useMemo(
    () =>
      !module.maxScoreIsSetByModule ||
      module.isFactModule ||
      module.isFinalExam,
    [module.isFactModule, module.isFinalExam, module.maxScoreIsSetByModule],
  );

  const maxActivityPointsDisabled = useMemo(
    () =>
      (!module.isFinalExam &&
        !module.isFactModule &&
        module.maxScoreIsSetByModule) ||
      (module.isFinalExam && module.maxScoreIsSetByModule),
    [module.isFactModule, module.isFinalExam, module.maxScoreIsSetByModule],
  );

  const matchedGy25Module = useMemo(
    () =>
      gy25Modules.find(
        (gy25m) =>
          gy25m.moduleCoupleId !== null &&
          gy25m.moduleCoupleId === module.moduleCoupleId &&
          gy25m.isFactModule !== module.isFactModule,
      ),
    [gy25Modules, module.isFactModule, module.moduleCoupleId],
  );

  const handleCheckQApiId = (qApiId) => {
    axios
      .get(`/Module/CheckQApiId?qApiId=${encodeURIComponent(qApiId)}`)
      .then((response) => {
        if (response.data && response.data.result) {
          setQApiIdCheckResult(response.data.result);
        } else {
          setQApiIdCheckResult({});
        }
      })
      .catch(() => {
        setQApiIdCheckResult({});
      });
  };

  const handleSetQApiId = (qApiId) => {
    if (qApiId.trim() === "") {
      setQApiIdCheckResult({});
      setModule((prev) => ({ ...prev, qApiId: "" }));
      return;
    }
    if (qApiId.length > 0) {
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
    }
    if (!qApiIdDisabled) {
      handleCheckQApiId(qApiId.trim());
    }
    setModule((prev) => ({ ...prev, qApiId }));
  };

  const handleSetFile = (file) => {
    setErrors(errors.filter((obj) => obj !== "file"));
    setModule({ ...module, file: file });
  };

  const handleSetGroupId = (selected) => {
    //Remove
    const highestModuleCoupleId = Math.max(
      ...gy25Modules.map((m) => m.moduleCoupleId).filter((id) => id !== null),
      0,
    );
    const newModuleCoupleId = highestModuleCoupleId + 1;
    if (selected.value === "remove") {
      setModule({ ...module, moduleCoupleId: null });
      if (
        gy25Modules.some(
          (m) =>
            m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id,
        )
      ) {
        const affectedModule = gy25Modules.find(
          (m) =>
            m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id,
        );
        affectedModule.moduleCoupleId = null;
        setModule({
          ...module,
          moduleCoupleId: null,
          affectedModules: [affectedModule],
        });
      } else {
        setModule({ ...module, moduleCoupleId: null });
      }
      return;
    }
    //Gör bara om koppling är ny
    if (
      module.moduleCoupleId !== selected.value.moduleCoupleId ||
      module.moduleCoupleId == null
    ) {
      if (module.moduleCoupleId == null) {
        //1 Modul har ingen koppling
        if (selected.value.moduleCoupleId == null) {
          //1.1 Modul har ingen koppling och vald modul har ingen koppling
          const affectedModule = selected.value;
          affectedModule.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [affectedModule],
          });
        } else {
          //1.2 Modul har ingen koppling men vald modul har koppling
          if (
            gy25Modules.some(
              (m) =>
                m.moduleCoupleId === selected.value.moduleCoupleId &&
                m.id !== selected.value.id,
            )
          ) {
            const moduleToDisconnect = gy25Modules.find(
              (m) =>
                m.moduleCoupleId === selected.value.moduleCoupleId &&
                m.id !== selected.value.id,
            );
            moduleToDisconnect.moduleCoupleId = null;
            const moduleToConnect = selected.value;
            moduleToConnect.moduleCoupleId = newModuleCoupleId;
            setModule({
              ...module,
              moduleCoupleId: newModuleCoupleId,
              affectedModules: [moduleToDisconnect, moduleToConnect],
            });
          }
        }
      } else {
        //2 Modul har koppling
        if (selected.value.moduleCoupleId == null) {
          //2.1 Modul har koppling men vald modul har ingen koppling
          const moduleToDisconnect = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id,
          );
          if (moduleToDisconnect != null) {
            moduleToDisconnect.moduleCoupleId = null;
          }
          const moduleToUpdate = selected.value;
          moduleToUpdate.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [
              moduleToDisconnect ?? moduleToDisconnect,
              moduleToUpdate,
            ],
          });
        } else {
          //2.2 Modul har koppling och vald modul har koppling
          const moduleToDisconnect = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id,
          );
          const moduleToDisconnectFromSelected = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === selected.value.moduleCoupleId &&
              m.id !== selected.value.id,
          );
          if (moduleToDisconnect != null) {
            moduleToDisconnect.moduleCoupleId = null;
          }
          if (moduleToDisconnectFromSelected != null) {
            moduleToDisconnectFromSelected.moduleCoupleId = null;
          }
          const moduleToConnect = selected.value;
          moduleToConnect.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [
              moduleToDisconnect ?? moduleToDisconnect,
              moduleToDisconnectFromSelected ?? moduleToDisconnectFromSelected,
              moduleToConnect,
            ],
          });
        }
      }
    }
  };
  const handleSetCoupleOptions = () => {
    let retList = [
      ...gy25Modules
        .filter((m) => m.isFactModule !== module.isFactModule)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((option) => ({
          ...option,
          label:
            option.moduleCoupleId !== null &&
            gy25Modules.some(
              (m) =>
                m.moduleCoupleId === option.moduleCoupleId &&
                m.id !== option.id,
            )
              ? option.name +
                intl.formatMessage({ id: "CurrentlyConnectedTo" }) +
                gy25Modules.find(
                  (m) =>
                    m.moduleCoupleId === option.moduleCoupleId &&
                    m.id !== option.id,
                ).name
              : option.name +
                " - " +
                (option.url.includes("Scormfiles/")
                  ? option.url
                      .split("Scormfiles/")[1]
                      .split("/index_lms.html")[0]
                  : option.url),
          value: option,
          clearableValue: true,
        })),
    ];
    const removeOption = {
      label: "➖ " + intl.formatMessage({ id: "RemovePairing" }),
      value: "remove",
    };
    if (moduleToEdit != null) {
      retList = [removeOption, ...retList];
    }
    return retList;
  };

  useEffect(() => {
    if (qApiIdDisabled) {
      setQApiIdCheckResult({});
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
      setModule((prev) => ({ ...prev, qApiId: "" }));
      return;
    }
  }, [qApiIdDisabled, setErrors, setModule]);

  useEffect(() => {
    if (qApiIdDisabled) return;

    if (qApiIdCheckResult.exists === false) {
      setErrors((prev) =>
        prev.includes("qApiId") ? prev : [...prev, "qApiId"],
      );
    } else if (qApiIdCheckResult.exists === true) {
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
    }
  }, [qApiIdDisabled, qApiIdCheckResult.exists, setErrors]);

  return (
    <FormGroup>
      {showPreviewCheckbox ? (
        <Row className="mt-3">
          <Col lg="4">
            <Label check>
              <Input
                type="checkbox"
                checked={!!module.isPreview}
                onChange={(e) => handleTogglePreview(e.target.checked)}
              />{" "}
              <FormattedMessage id="Preview" />
            </Label>
          </Col>
          <Col></Col>
        </Row>
      ) : null}
      {module.isPreview ? (
        <Row className="mt-3">
          <Col lg="4">
            <Label for="PreviewText" className="mt-2">
              <FormattedMessage id="TextToTeacher" />
            </Label>
          </Col>
          <Col>
            <Input
              name="PreviewText"
              type="text"
              maxLength={50}
              placeholder="t.ex. Kommer januari 2027"
              value={module.previewText ?? ""}
              onChange={(e) => handleSetPreviewText(e.target.value)}
              className={errors.includes("previewText") ? "input--withWarning" : ""}
            />
          </Col>
        </Row>
      ) : null}
      <Row className="mt-3">
        <Col lg="4">
          <Label for="File">Scorm-zip</Label>
        </Col>
        <Col>
          <Input
            type="file"
            disabled={module.isPreview}
            onChange={(e) => handleSetFile(e.target.files[0])}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="Answers">
            <FormattedMessage id="AnswersPdf" />
          </Label>
        </Col>
        <Col>
          <Input
            type="file"
            disabled={module.isPreview || (module.isFactModule !== module.maxScoreIsSetByModule)}
            onChange={(e) =>
              setModule({ ...module, answers: e.target.files[0] })
            }
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="Name" className="mt-2">
            <FormattedMessage id="Name" />
          </Label>
        </Col>
        <Col>
          <Input
            name="Name"
            type="text"
            className={errors.includes("name") ? "input--withWarning" : ""}
            value={module.name}
            onChange={(data) => handleSetName(data.target.value)}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="Description" className="mt-2">
            <FormattedMessage id="Description" />
          </Label>
        </Col>
        <Col>
          <Input
            name="Description"
            type="textarea"
            className={
              errors.includes("description") ? "input--withWarning" : ""
            }
            value={module.description}
            onChange={(data) => handleSetDescription(data.target.value)}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="ApproveScoreLimit" className="mt-2">
            <FormattedMessage id="ApproveScoreLimitInPercentage" />
          </Label>
        </Col>
        <Col>
          <Input
            disabled={module.isPreview || moduleToEdit != null}
            title={
              moduleToEdit != null
                ? intl.formatMessage({
                    id: "ContactAdministrator",
                  })
                : ""
            }
            name="ApproveScoreLimit"
            type="number"
            value={module.approveScoreLimit}
            onChange={(data) =>
              setModule({
                ...module,
                approveScoreLimit: data.target.value,
              })
            }
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="MaxScore" className="mt-2">
            <FormattedMessage id="MaxScore" />
          </Label>
        </Col>
        <Col>
          <Input
            name="MaxScore"
            type="number"
            value={!module.maxScoreIsSetByModule ? module.maxScore : ""}
            onChange={(data) =>
              setModule({
                ...module,
                maxScore: data.target.value,
              })
            }
            disabled={module.isPreview || module.maxScoreIsSetByModule}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="MaxActivityPoints" className="mt-2">
            <FormattedMessage id="MaxActivityPoints" />
          </Label>
        </Col>
        <Col>
          <Input
            name="MaxActivityPoints"
            type="number"
            value={!maxActivityPointsDisabled ? module.maxActivityPoints : ""}
            onChange={(data) => {
              setModule({
                ...module,
                maxActivityPoints: data.target.value,
              });
            }}
            disabled={module.isPreview || maxActivityPointsDisabled}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="IsFinalExam" className="mt-2">
            <FormattedMessage id="IsFinalExam" />
          </Label>
        </Col>
        <Col>
          <Input
            className="ms-0 me-3 mt-3"
            name="Name"
            type="checkbox"
            checked={module.isFinalExam}
            onChange={(data) =>
              setModule({ ...module, isFinalExam: data.target.checked })
            }
            disabled={module.isPreview || module.isFactModule}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="MaxScoreIsSetByModule" className="mt-2">
            <FormattedMessage id="MaxScoreIsSetByModule" />
          </Label>
        </Col>
        <Col>
          <Input
            className="ms-0 me-3 mt-3"
            name="MaxScoreIsSetByModule"
            type="checkbox"
            checked={module.maxScoreIsSetByModule}
            onChange={(data) => {
              setModule({
                ...module,
                maxScoreIsSetByModule: data.target.checked,
                answers:
                  data.target.checked === module.isFactModule
                    ? module.answers
                    : null,
              });
            }}
            disabled={module.isPreview}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="IsFactModule" className="mt-2">
            <FormattedMessage id="FactModule" />
          </Label>
        </Col>
        <Col>
          <Input
            className="ms-0 me-3 mt-3"
            name="IsFactModule"
            type="checkbox"
            checked={factModuleDisabled ? false : module.isFactModule}
            onChange={(data) => {
              setModule({
                ...module,
                qApiId: data.target.checked ? "" : module.qApiId,
                isFactModule: data.target.checked,
                answers:
                  data.target.checked === module.maxScoreIsSetByModule
                    ? module.answers
                    : null,
              });
            }}
            disabled={module.isPreview || factModuleDisabled}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="QApiId" className="mt-2">
            <FormattedMessage id="QApiId" />
          </Label>
        </Col>
        <Col>
          <Input
            name="QApiId"
            className={
              errors.includes("qApiId") && !qApiIdDisabled
                ? "input--withWarning"
                : ""
            }
            type="text"
            maxLength={21}
            value={module.qApiId}
            onChange={(data) => handleSetQApiId(data.target.value)}
            disabled={module.isPreview || qApiIdDisabled}
          ></Input>
          {qApiIdCheckResult.hasQApiFetchError ? (
            <div className="text-danger input-feedback">
              <FormattedMessage id="CouldNotValidateQApiId" />
            </div>
          ) : qApiIdCheckResult.exists === false ? (
            <div className="text-danger input-feedback">
              <FormattedMessage id="QApiIdNotExists" />
            </div>
          ) : qApiIdCheckResult.exists === true ? (
            <div className="text-success input-feedback">
              {qApiIdCheckResult.qApiId} - {qApiIdCheckResult.name}
            </div>
          ) : null}
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="CoupleId" className="mt-2">
            <FormattedMessage id="CoupleId" />
          </Label>
        </Col>
        <Col>
          <Input
            name="CoupleId"
            type="number"
            value={module.moduleCoupleId}
            disabled={true}
          ></Input>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="4">
          <Label for="Couple" className="mt-2">
            <FormattedMessage id="ConnectedTo" />
          </Label>
        </Col>
        <Col>
          <Select
            placeholder={intl.formatMessage({ id: "ChooseModuleToCouple" })}
            defaultValue={
              matchedGy25Module
                ? { value: matchedGy25Module, label: matchedGy25Module.name }
                : ""
            }
            noOptionsMessage={() => intl.formatMessage({ id: "NoProgramsToChoose" })}
            name="Couple"
            onChange={(selected) => selected && handleSetGroupId(selected)}
            options={handleSetCoupleOptions()}
            isDisabled={module.isPreview || module.isFinalExam || !module.maxScoreIsSetByModule}
            isClearable
          />
        </Col>
      </Row>
    </FormGroup>
  );
};
export default CreateNewScormModuleForm;
